import { WSActions, WSAlert, WSList } from "@wingspanhq/fe-component-library";
import {
  AttributeDataType,
  WorkDefinition
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useQueryPayerEngagement } from "../../../../query/payerEngagements/queries/useQueryPayerEngagement";
import { queriesPayers, queriesWorkItems, queriesWorkLogs } from "@query";
import { selectorPayerResponseFromV3 } from "@selectors/selectorPayerResponseFromV3";
import { useQueryPayerRows } from "../../../../query/search/payer/queries/useQueryPayerRows";
import { useQueryEngagementsWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryEngagementWorkDefinitions";
import { Layout } from "../../../../shared/components/Layout";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { selectorPayerDisplayName } from "../../../../shared/selectors/selectorPayerDisplayName";
import { CardWorkItems } from "../../components/CardWorkItems";
import { filterActiveWorkItems } from "../../utils/filterActiveWorkItems";
import { WorkLogEngagementDetails } from "../../components/WorkLogEngagementDetails";
import { useModalConvert } from "../../modals/ModalConvert";
import { useModalExit } from "./ModalExit";
import { useQueryInvoicingConfigForPayee } from "../../../../query/invoicingConfig/queries/useQueryInvoicingConfigForPayee";
import { selectorIsPayeeAllowedToInitiateInvoice } from "../../../../Invoices/selectors/selectorIsPayeeAllowedToInitiateInvoice";
import { AlertClientNotAllowedSubmission } from "../../../../Invoices/components/AlertClientNotAllowedSubmission";
import { useSession } from "@hooks/useSession";

export const RouteEdit: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const { accountId } = useSession();
  const history = useHistory();
  const workLogId = props.match.params.workLogId;
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery({ workLogId });
  const workLog = queryWorkLog.data;

  // TODO(v3): v1 `payerPayeeEngagementId` renamed to `payeeEngagementId`.
  const queryPayerRow = useQueryPayerRows(
    {
      filter: {
        "engagements.payerPayeeEngagementId": workLog?.payeeEngagementId
      }
    },
    {
      enabled: !!workLog?.payeeEngagementId
    }
  );

  const payerId = queryPayerRow.data?.[0]?.payerId;
  const payerPayeeEngagementId = workLog?.payeeEngagementId;

  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: payerId! },
    { enabled: !!payerId, retry: false }
  );
  const queryPayerPayeeEngagement = useQueryPayerEngagement(
    payerId!,
    payerPayeeEngagementId!,
    {
      enabled: !!payerId && !!payerPayeeEngagementId
    }
  );
  const payer = queryPayer.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  const queryWorkDefinitions = useQueryEngagementsWorkDefinitions(
    {
      payerId: payerId!,
      engagementId: payerPayeeEngagementId!,
      sort: { name: "asc" }
    },
    {
      enabled: !!(payerId && payerPayeeEngagementId)
    }
  );

  const queryInvoicingConfigForPayee = useQueryInvoicingConfigForPayee(
    payerId as string,
    {
      enabled: !!payerId
    }
  );

  const isPayeeNotAllowedToInitiateInvoice =
    !selectorIsPayeeAllowedToInitiateInvoice(
      queryInvoicingConfigForPayee.data,
      accountId
    );

  // v3 `listWorkDefinitions` is account-scoped, so the payee can't list the
  // payer's work definitions off it — keep the v1 payerEngagements endpoint and
  // adapt its shape to v3 `WorkDefinition` for the (now v3) CardWorkItems.
  // TODO(v3): payee-side work-definition read stays on v1 payerEngagements; no
  // account-scoped v3 source exists for a payer's work definitions.
  const workDefinitions = useMemo<WorkDefinition[]>(
    () =>
      (queryWorkDefinitions.data || []).map(
        wd =>
          ({
            id: wd.workDefinitionId,
            name: wd.name,
            description: wd.description,
            attributeDefinitions: (wd.attributeDefinitions || []).map(attr => ({
              key: attr.key,
              name: attr.name,
              description: attr.description,
              type: attr.type as unknown as AttributeDataType,
              isRequired: attr.required,
              validationRules: attr.validationRules
            }))
          }) as unknown as WorkDefinition
      ),
    [queryWorkDefinitions.data]
  );

  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId) {
      return "Log Work";
    }

    if (!payer) {
      return "Log Work";
    }

    const payerName = selectorPayerDisplayName(payer);

    if (payerPayeeEngagement) {
      return `Log Work – ${payerName} • ${payerPayeeEngagement.engagementName}`;
    }

    return `Log Work – ${payerName}`;
  }, [payer, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  const queryWorkItems = queriesWorkItems.listWorkItems.useQuery({
    filter: { workLogId: { eq: workLogId } }
  });

  const modalExit = useModalExit();
  const modalConvert = useModalConvert();

  const [createError, setCreateError] = useState(false);

  // TODO(v3): `invoiceId` renamed to `convertedResourceId` on v3 WorkLog.
  const isInvoiceCreated = !!workLog?.convertedResourceId;

  return (
    <Layout
      title="Work log"
      onClose={() => {
        modalExit.open();
      }}
    >
      <WSList gap="2XL">
        <WorkLogEngagementDetails
          shimmer={queryPayerPayeeEngagement.isInitialLoading}
          payer={payer ? selectorPayerResponseFromV3(payer) : undefined}
          payerPayeeEngagement={payerPayeeEngagement}
        />

        {isPayeeNotAllowedToInitiateInvoice && (
          <AlertClientNotAllowedSubmission />
        )}

        <CardWorkItems
          shimmer={
            queryWorkItems.isInitialLoading ||
            queryWorkDefinitions.isInitialLoading
          }
          noEdit={isInvoiceCreated}
          workLogId={workLogId}
          payerId={payerId!}
          payerPayeeEngagementId={payerPayeeEngagementId!}
          workItems={queryWorkItems.data?.data || []}
          workDefinitions={workDefinitions}
          resources={{
            payerPayeeEngagement
          }}
        />

        {createError && (
          <WSAlert
            title="Invoice creation error"
            theme="warning"
            icon="alert"
            onDismiss={() => {
              setCreateError(false);
            }}
          >
            There was an error creating the invoice. Please try again. If the
            issue persists, please contact support.
          </WSAlert>
        )}

        <WSActions
          alignment="fill"
          disabled={isPayeeNotAllowedToInitiateInvoice}
          buttons={[
            {
              label: isInvoiceCreated ? "View invoice" : "Create invoice",
              disabled:
                filterActiveWorkItems(queryWorkItems.data?.data || [])
                  .length === 0,
              onClick: () => {
                if (isInvoiceCreated) {
                  history.push(
                    `/member/invoices/${workLog.convertedResourceId}`
                  );
                } else {
                  modalConvert.open({
                    workLogId,
                    onClose: result => {
                      setCreateError(result?.createError || false);
                    }
                  });
                }
              }
            }
          ]}
        />
      </WSList>
    </Layout>
  );
};
